/* eslint-disable max-len */
/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo } from 'react';

import { omit } from 'lodash';
import { useAdminParams } from 'utils/helpers';
import { Logs } from 'utils/types';
import Cookies from 'js-cookie';

import { Actor, ActivityLogData, ActivityLogDataDetails } from '../ActivityLog';

const CompanyLogs = {
  COMPANY_CREATED: {
    title: 'Create Company',
    element: ({ actor }: Logs) => (
      <p>
        <Actor data={actor} /> created the Company.
      </p>
    ),
  },
  COMPANY_UPDATED: {
    title: 'Edit Company',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const _meta = omit(meta, [
        'updatedAt',
        'accountId',
        'createdAt',
        'trashed',
        'trashedAt',
        'data',
        'dataList',
        '__v',
      ]);
      const _prevMeta = omit(prevMeta, [
        'updatedAt',
        'accountId',
        'createdAt',
        'trashed',
        'trashedAt',
        'data',
        'dataList',
        '__v',
      ]);

      const data = {
        ..._meta,
        tagId:
          _meta.tagId.map(({ tag }: Record<string, any>) => tag).join(', ') ??
          [],
      };

      const prevData = {
        ..._prevMeta,
        tagId:
          _prevMeta?.tagId
            .map(({ tag }: Record<string, any>) => tag)
            .join(', ') ?? [],
      };

      return (
        <>
          <p>
            <Actor data={actor} /> updated the Company.
          </p>
          <ActivityLogData data={data} prevData={prevData} />
        </>
      );
    },
  },
  COMPANY_DELETED: {
    title: 'Delete Company',
    element: ({ actor }: Logs) => (
      <p>
        <Actor data={actor} /> deleted the Company.
      </p>
    ),
  },
  COMPANY_LEAD_ASSIGNED: {
    title: 'Associate Company To Lead',
    element: ({ actor, meta: { lead } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/contact/leads/${lead._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${lead._id}`;
        }

        if (actor?.role === 'account') return `/contact/leads/${lead._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${lead._id}`;
      }, [actor?.role, role, accountId, accountUserId, lead]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> assgined the Company to Lead.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_LEAD_REMOVED_ASSIGNED: {
    title: 'Remove Associate Company To Lead',
    element: ({ actor, meta: { lead } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/contact/leads/${lead?._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${lead?._id}`;
        }

        if (actor?.role === 'account') return `/contact/leads/${lead?._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${lead?._id}`;
      }, [actor?.role, role, accountId, accountUserId, lead]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> assgined the Company from Lead.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_DEAL_ASSIGNED: {
    title: 'Associate Company To Deal',
    element: ({ actor, meta: { deal } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/sales/deals/${deal._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal._id}`;
        }

        if (actor?.role === 'account') return `/sales/deals/${deal._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal._id}`;
      }, [actor?.role, role, accountId, accountUserId, deal]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> assgined the Company to Deal.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_DEAL_REMOVED_ASSIGNED: {
    title: 'Remove Associate Company To Deal',
    element: ({ actor, meta: { deal } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/sales/deals/${deal._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal._id}`;
        }

        if (actor?.role === 'account') return `/sales/deals/${deal._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal._id}`;
      }, [actor?.role, role, accountId, accountUserId, deal]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> unassigned the Company from Deal.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_ADDED: {
    title: 'Add Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_COMMENT_ADDED: {
    title: 'Add Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_UPDATED: {
    title: 'Edit Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, referenceId, accountId, accountUserId, comment]);
      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> updated a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_COMMENT_UPDATED: {
    title: 'Edit Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);
      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> updated a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_DELETED: {
    title: 'Delete Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, referenceId, accountUserId, comment]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> deleted a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_COMMENT_DELETED: {
    title: 'Delete Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> deleted a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_ATTACHMENT_ADDED: {
    title: 'Add Note Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
      ]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added attachment file to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_ATTACHMENT_DELETED: {
    title: 'Delete Note Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
      ]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_COMMENT_ATTACHMENT_ADDED: {
    title: 'Add Comment Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
      ]);
      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added attachment file to a comment.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_COMMENT_COMMENT_ATTACHMENT_DELETED: {
    title: 'Delete Comment Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
      ]);
      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a comment.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_ATTACHMENT_ADDED: {
    title: 'Add Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
      ]);
      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added attachment file to a Company.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  COMPANY_ATTACHMENT_DELETED: {
    title: 'Delete Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/companies/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/companies/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        referenceId,
        attachment._id,
      ]);
      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return false;
      }, [actor?.role]);
      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a Company.
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
};

export default CompanyLogs;
